import { useEffect, useRef, useState } from 'react';
import { useCheckoutCartState } from '@monofront/business/contexts';

import PaypalButtonContainer from '@components/PaypalButtonContainer';
import { PAYPAL_FUNDINGS } from '@monofront/business/constants';
import { useCheckoutPayment } from '../contexts';
import { CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL } from '@monofront/business/queries';
import { useMutation } from '@apollo/client';
import { ICreatePaymentForNewPaypalIntentQueryResult } from '@monofront/types';
import useCompleteOrder from '../hooks/useCompleteOrder';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import { PayPalButtons } from '@paypal/react-paypal-js';

/**
 * Display a Pay CTA to pay with a new Paypal account (not for MAAP flow)
 */
const PayWithNewPaypalCta: React.FC = () => {
  const { checkoutCart } = useCheckoutCartState();
  const { setIsOrderValidationInProgress, selectedNewPaypal } =
    useCheckoutPayment();
  const [hideButton, setHideButton] = useState(true); // hide paypal button while not ready for interaction
  const cartId = useRef('');
  const { completeOrder } = useCompleteOrder();
  const { handlePaymentError } = useHandlePaymentError();

  const [createPaymentIntentForNewPaypal] =
    useMutation<ICreatePaymentForNewPaypalIntentQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL,
    );

  useEffect(() => {
    // enable paypal button if new cart
    if (cartId.current !== checkoutCart?.id) {
      setHideButton(false);
    }
  }, [cartId.current]);

  if (
    !checkoutCart?.id ||
    !checkoutCart?.payment?.id ||
    !selectedNewPaypal?.paymentMethodCode
  )
    return null;

  return (
    <PaypalButtonContainer hideButton={hideButton}>
      <PayPalButtons
        // force remount component when the founding source changes (paypal to paylater, on checkbox selection), as the PaypalButton component doesn't remount by itself
        key={PAYPAL_FUNDINGS[selectedNewPaypal.paypalType]}
        fundingSource={PAYPAL_FUNDINGS[selectedNewPaypal.paypalType]}
        onInit={() => {
          setHideButton(false);
        }}
        createOrder={async () => {
          setIsOrderValidationInProgress(true);

          const { data } = await createPaymentIntentForNewPaypal({
            variables: {
              tokenValue: checkoutCart.id,
              cartPaymentId: checkoutCart.payment?.id,
              paymentMethodCode: selectedNewPaypal.paymentMethodCode,
            },
          });

          if (!data?.createPaymentIntentForNewPaypal?.payment?.paypalOrderId) {
            throw new Error(
              'Create Payment Intent failed with empty response.',
            );
          }

          cartId.current = data.createPaymentIntentForNewPaypal.id;

          // start paypal experience
          return data.createPaymentIntentForNewPaypal.payment.paypalOrderId;
        }}
        onApprove={async () => {
          await completeOrder();
        }}
        onCancel={() => {
          setHideButton(false);
          setIsOrderValidationInProgress(false);
        }}
        onError={(error) => {
          setHideButton(false);
          setIsOrderValidationInProgress(false);

          const isApolloError =
            typeof error?.stack === 'string' &&
            error.stack.includes('ApolloError');

          handlePaymentError({
            code: 'PAY_WITH_NEW_PAYPAL_FAILED',
            message:
              !isApolloError && typeof error?.message === 'string'
                ? error.message
                : 'Cannot pay with Paypal',
            shouldDisplayError: true,
            error,
            context: {
              paypal_method_type: selectedNewPaypal?.paypalType,
            },
          });
        }}
        style={{
          layout: 'horizontal',
          tagline: false,
          shape: 'pill',
          color: 'gold',
        }}
      />
    </PaypalButtonContainer>
  );
};

export default PayWithNewPaypalCta;
